import React, { useState } from 'react';
import axios from '../api/axiosConfig';
import './Modal.css';

const ImportExcelModal = ({ isOpen, onClose, onImportSuccess, loading }) => {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [results, setResults] = useState(null);
  const [showResults, setShowResults] = useState(false);

  // Limpiar el estado cuando el modal se abre
  React.useEffect(() => {
    if (isOpen) {
      setFile(null);
      setError('');
      setSuccess('');
      setResults(null);
      setShowResults(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  }, [isOpen]);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      const ext = selectedFile.name.split('.').pop().toLowerCase();
      if (['xlsx', 'xls', 'csv'].includes(ext)) {
        setFile(selectedFile);
        setError('');
        setSuccess('');
        setResults(null);
        setShowResults(false);
      } else {
        setError('Solo se permiten archivos Excel (.xlsx, .xls) o CSV');
        setFile(null);
      }
    }
  };

  const fileInputRef = React.useRef(null);

  const triggerFileInput = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleImport = async (e) => {
    e.preventDefault();
    
    if (!file) {
      setError('Por favor selecciona un archivo');
      return;
    }

    setUploading(true);
    setError('');
    setSuccess('');

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post('/api/cupl/import/excel', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });

      setResults(response.data.results);
      setSuccess(`✅ Importación completada: ${response.data.results.success} registros importados`);
      setFile(null);
      setShowResults(true);

      // Si no hay errores, cerramos automáticamente. Si hay errores, dejamos abierto para que el usuario los vea.
      if (response.data.results.failed === 0) {
        setTimeout(() => {
          onImportSuccess();
          onClose();
        }, 2000);
      } else {
        onImportSuccess(); // Actualizamos la tabla de fondo igual
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Error al importar archivo');
    } finally {
      setUploading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={() => !uploading && onClose()}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>📥 Importar CUPL desde Excel</h2>
          <button 
            className="modal-close" 
            onClick={onClose}
            disabled={uploading}
          >
            ✕
          </button>
        </div>

        <form onSubmit={handleImport} className="modal-form">
          {error && <div className="error-message">{error}</div>}
          {success && <div className="success-message">{success}</div>}

          <div className="form-group">
            <label>Seleccionar archivo:</label>
            <div 
              className="file-input-wrapper"
              onClick={triggerFileInput}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  triggerFileInput();
                }
              }}
            >
              <input
                ref={fileInputRef}
                type="file"
                id="file-input"
                onChange={handleFileChange}
                accept=".xlsx,.xls,.csv"
                disabled={uploading}
                style={{ display: 'none' }}
              />
              <span className="file-name">
                {file ? `✓ ${file.name}` : 'Seleccionar archivo...'}
              </span>
            </div>
            <small>Formatos: .xlsx, .xls, .csv</small>
          </div>

          {showResults && results && (
            <div className="import-results">
              <h4>Resultados de importación:</h4>
              <div className="results-grid">
                <div className="result-item success">
                  <div className="result-number">{results.success}</div>
                  <div>Importados</div>
                </div>
                {results.failed > 0 && (
                  <div className="result-item error">
                    <div className="result-number">{results.failed}</div>
                    <div>Fallidos</div>
                  </div>
                )}
              </div>
              
              {results.errors && results.errors.length > 0 && (
                <div className="error-list-container" style={{ marginTop: '15px', maxHeight: '200px', overflowY: 'auto', border: '1px solid #fee2e2', borderRadius: '6px', padding: '10px', background: '#fff' }}>
                  <p style={{ margin: '0 0 10px 0', fontSize: '13px', fontWeight: 'bold', color: '#7f1d1d' }}>Detalles de errores:</p>
                  <ul style={{ margin: 0, padding: '0 0 0 20px', fontSize: '12px', color: '#b91c1c' }}>
                    {results.errors.map((err, idx) => (
                      <li key={idx} style={{ marginBottom: '4px' }}>{err}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          <div className="modal-actions">
            <button
              type="button"
              className="btn btn-cancel"
              onClick={onClose}
              disabled={uploading}
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="btn btn-save"
              disabled={uploading || !file}
            >
              {uploading ? '⏳ Importando...' : '📤 Importar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ImportExcelModal;
